/**
 * lifespanPredictor.js — Battery Life Predictor
 *
 * Turns EVIQ's existing degradation math into a proper lifespan forecast:
 * total years of usable life remaining, projected end-of-life date, miles-
 * of-headroom, and a plain-English milestone timeline (80% → 70% → 60%).
 *
 * Distinct from `forecastDegradation` (which returns raw crossing months)
 * in that this function is presentation-focused: dates, deltas from today,
 * confidence, and a narrative sentence ready for the UI.
 *
 * All math is deterministic and client-side. No round-trip needed.
 */

const MS_PER_MONTH = 30.44 * 24 * 60 * 60 * 1000;

/** Retire the pack at 60% SoH — where most owners agree the vehicle stops
 *  being practical as a daily driver and range anxiety becomes daily anxiety. */
const END_OF_LIFE_THRESHOLD = 60;

const zoneImpact = {
  "hot-desert":    1.35,
  "hot-humid":     1.15,
  "temperate":     1.00,
  "mild-coastal":  0.90,
  "cold-north":    1.05,
  "high-elevation":1.02,
};

/**
 * Predict remaining battery life.
 *
 * @param {object} inp
 * @param {number} inp.currentSoh        current SoH % (e.g., 91.2)
 * @param {number} inp.monthlyLoss       recent 30-day loss %/mo (positive number, e.g., 0.22)
 * @param {string} inp.climateZone       one of zoneImpact keys
 * @param {number} inp.vehicleAgeYears   how old the car is right now (e.g., 3.2)
 * @param {number} inp.milesPerYear      annual miles driven (used for miles projection)
 * @param {number} inp.warrantyThreshold Tesla warranty capacity floor (default 70)
 * @param {number} inp.warrantyYears     Tesla warranty year cap (default 8)
 * @param {number} inp.warrantyMiles     Tesla warranty mileage cap (default 120_000)
 * @param {number} inp.dataMonths        months of telemetry we have (drives confidence)
 * @returns {object}
 */
export function predictLifespan({
  currentSoh,
  monthlyLoss,
  climateZone = "temperate",
  vehicleAgeYears = 3,
  milesPerYear = 12000,
  warrantyThreshold = 70,
  warrantyYears = 8,
  warrantyMiles = 120000,
  dataMonths = 6,
  brandName = "Tesla",
} = {}) {
  const zone = zoneImpact[climateZone] || 1.0;

  // Effective monthly loss with mild climate weighting, clamped to the
  // physically plausible fleet band (0.02–0.6 %/mo). Real batteries curve
  // gently (√t), so a linear projection at today's rate is already the
  // conservative bound; letting outlier inputs through just destroys trust.
  const effectiveLoss = Math.min(0.6, Math.max(0.02, monthlyLoss * zone));

  // Months until we hit each threshold.
  const monthsToLoseTo = (target) => {
    if (currentSoh <= target) return 0;
    return Math.max(0, (currentSoh - target) / effectiveLoss);
  };

  const toDate = (months) => {
    const d = new Date(Date.now() + months * MS_PER_MONTH);
    return d;
  };
  const fmtDate = (d) =>
    d.toLocaleDateString("en-US", { year: "numeric", month: "short" });

  const now = new Date();

  // ---- Milestones (80%, 70%, 60%) ----
  const milestoneDefs = [
    { threshold: 80, event: "Warranty ‘safe zone’ crossed", severity: "info" },
    { threshold: 70, event: `${brandName} warranty floor`, severity: "warn" },
    { threshold: 60, event: "Practical end-of-life", severity: "critical" },
  ];

  const milestones = milestoneDefs.map((m) => {
    const monthsAway = monthsToLoseTo(m.threshold);
    const date = toDate(monthsAway);
    return {
      ...m,
      monthsAway: +monthsAway.toFixed(1),
      yearsAway: +(monthsAway / 12).toFixed(1),
      date,
      dateLabel: fmtDate(date),
      already: monthsAway === 0,
    };
  });

  // ---- Headline: total remaining life ----
  const monthsToEOL = monthsToLoseTo(END_OF_LIFE_THRESHOLD);
  const yearsRemaining = +(monthsToEOL / 12).toFixed(1);
  const endOfLifeDate = toDate(monthsToEOL);
  const milesRemaining = Math.round((monthsToEOL / 12) * milesPerYear);

  // ---- Warranty crossover assessment ----
  const monthsToWarranty = monthsToLoseTo(warrantyThreshold);
  const yearsToWarranty = +(monthsToWarranty / 12).toFixed(1);
  const currentMiles = Math.round(vehicleAgeYears * milesPerYear);
  const projectedMilesAtWarrantyCross =
    currentMiles + Math.round((monthsToWarranty / 12) * milesPerYear);
  // Warranty ends whichever comes first — years OR miles.
  const monthsUntilWarrantyEnds = Math.max(
    0,
    (warrantyYears - vehicleAgeYears) * 12
  );
  const yearsUntilWarrantyEnds = +(monthsUntilWarrantyEnds / 12).toFixed(1);
  const monthsUntilMileageCap = Math.max(
    0,
    ((warrantyMiles - currentMiles) / milesPerYear) * 12
  );
  const warrantyWindowMonths = Math.min(
    monthsUntilWarrantyEnds,
    monthsUntilMileageCap
  );

  const willQualifyForWarranty =
    monthsToWarranty > 0 && monthsToWarranty <= warrantyWindowMonths;

  // ---- Confidence rating ----
  //   ≥12mo data → high, 6-11mo → medium, <6mo → low
  const confidence =
    dataMonths >= 12 ? "high" : dataMonths >= 6 ? "medium" : "low";

  // ---- Plain-English narrative ----
  const narrative = buildNarrative({
    yearsRemaining,
    endOfLifeDateLabel: fmtDate(endOfLifeDate),
    willQualifyForWarranty,
    yearsToWarranty,
    yearsUntilWarrantyEnds,
    effectiveLoss,
    climateZone,
    milesRemaining,
    confidence,
    brandName,
  });

  return {
    // Headline
    yearsRemaining,
    endOfLifeDate,
    endOfLifeDateLabel: fmtDate(endOfLifeDate),
    endOfLifeThreshold: END_OF_LIFE_THRESHOLD,
    milesRemaining,
    // Warranty
    yearsToWarranty,
    warrantyThreshold,
    yearsUntilWarrantyEnds,
    warrantyMilesCap: warrantyMiles,
    projectedMilesAtWarrantyCross,
    willQualifyForWarranty,
    // Milestones
    milestones,
    // Diagnostics
    effectiveMonthlyLoss: +effectiveLoss.toFixed(3),
    climateMultiplier: +zone.toFixed(2),
    confidence,
    // Copy
    narrative,
  };
}

function buildNarrative({
  yearsRemaining,
  endOfLifeDateLabel,
  willQualifyForWarranty,
  yearsToWarranty,
  yearsUntilWarrantyEnds,
  effectiveLoss,
  climateZone,
  milesRemaining,
  confidence,
  brandName = "Tesla",
}) {
  const climateLabel = (climateZone || "temperate").replace(/-/g, " ");
  const pace =
    effectiveLoss < 0.15
      ? "gentler than the fleet median"
      : effectiveLoss < 0.25
      ? "in line with the fleet median"
      : "faster than the fleet median";

  const parts = [];
  parts.push(
    `At today's pace (${effectiveLoss.toFixed(2)}%/mo, ${pace}), your pack has roughly ${yearsRemaining} years of practical daily-driver life left — projected end-of-life ${endOfLifeDateLabel}, or about ${milesRemaining.toLocaleString()} more miles.`
  );

  if (willQualifyForWarranty) {
    if (confidence === "low") {
      parts.push(
        `If this early pace held, the curve would cross ${brandName}'s 70% warranty floor in ~${yearsToWarranty} years, inside the warranty window (~${yearsUntilWarrantyEnds} years remaining). Early projections almost always soften as real telemetry accumulates — EVIQ will keep the evidence trail ready either way.`
      );
    } else {
      parts.push(
        `Your degradation curve is on track to cross ${brandName}'s 70% warranty floor in ${yearsToWarranty} years — inside the warranty window (~${yearsUntilWarrantyEnds} years remaining). That means a warranty claim is plausible; EVIQ will keep the evidence trail ready.`
      );
    }
  } else if (yearsToWarranty > 0) {
    parts.push(
      `Your pack is unlikely to cross ${brandName}'s 70% warranty floor before the warranty window closes (~${yearsUntilWarrantyEnds} years remaining). That's actually good news — you're aging well for a ${climateLabel} climate.`
    );
  }

  if (confidence === "low") {
    parts.push(
      `Note: this is an early-days projection based on limited telemetry. Confidence will improve after 6+ months of continuous sync.`
    );
  }

  return parts.join(" ");
}
